import Button from '@doordash/component-button'
import { NumberField } from '@doordash/component-fields'
import NavBar from '@doordash/component-navbar'
import {
  Text,
  InlineChildren,
  Stack,
  StackChildren,
} from '@doordash/design-language'
import { noop } from 'lodash'
import * as React from 'react'
import { connect } from 'react-redux'
import { RouteComponentProps, withRouter } from 'react-router'
import * as actions from '../../../actions'
import * as events from '../../../events'
import Form from '../../../utilities/Form'
import { validators as sharedValidators } from '../../../utilities/validators'

import RedCard from '../../atoms/images/redcard'

import { Bottom } from '../redcard-begin/styles'
import { Wrapper } from './styles'

import { detectWebviewOs } from '../../../utilities/userAgent'
import { OsTypes } from '../../../webview/types'

import LearnMoreModal from './learn-more-modal'
import t from './strings'

type RedCardActivateType = {
  dasherId: string
  redCardIsSubmitting?: object
  experiments?: any
  onActivate?: () => void
  returnLink?: () => void | string
  activateRedCard?: (
    dasherId: string,
    last4: number,
    delightNumber: number
  ) => Promise<any>
  error?: string
  clearErrors?: () => void
  getRedCard?: (dasherId: string) => Promise<any>
  trackRedCardActivateRendered?: () => Promise<any>
  trackRedCardActivateSubmit?: () => Promise<any>
  trackRedCardActivateError?: (error) => Promise<any>
  trackRedCardActivateGoBack?: () => Promise<any>
  trackRedCardActivateLearnMore?: (state) => Promise<any>
}

type RedCardActivateState = {
  initialHeight: number
  showSubmitButton: boolean
  isShowingModal: boolean
}

export class RedCardActivate extends React.Component<
  RedCardActivateType & RouteComponentProps<any>,
  RedCardActivateState
> {
  public static defaultProps = {
    experiments: {},
    redCardIsSubmitting: { isSubmitting: false },
    onActivate: noop,
    activateRedCard: noop,
    getRedCard: noop,
    trackRedCardActivateRendered: noop,
    trackRedCardActivateSubmit: noop,
    trackRedCardActivateError: noop,
    trackRedCardActivateGoBack: noop,
    trackRedCardActivateLearnMore: noop,
  }

  public static fields = {
    last4: '',
    delightNumber: '',
  }

  public static validators: any = {
    last4: sharedValidators.redCardLastFour,
    delightNumber: sharedValidators.redCardDelightNumber,
  }

  public state = {
    initialHeight: 0,
    showSubmitButton: true,
    isShowingModal: false,
  }

  constructor(props) {
    super(props)

    // Because the submit button is fixed to the bottom and
    // opening the virtual keyboard adjusts the viewport,
    // we want to hide the submission button when the virtual
    // keyboard is open
    const os = detectWebviewOs()
    if (os === OsTypes.Android) {
      window.addEventListener('resize', this.handleScroll)
    }
  }

  public componentDidMount() {
    const { trackRedCardActivateRendered } = this.props
    trackRedCardActivateRendered()

    this.setState({ initialHeight: window.innerHeight })

    document.ontouchmove = e => {
      e.preventDefault()
    }
  }

  public componentWillUnmount() {
    window.removeEventListener('resize', this.handleScroll)
  }

  public handleScroll = e => {
    const HIDE_BUTTON_RATIO = 1.4
    const heightRatio = this.state.initialHeight / e.target.innerHeight

    if (heightRatio < HIDE_BUTTON_RATIO) {
      this.setState({ showSubmitButton: true })
    } else {
      this.setState({ showSubmitButton: false })
    }
  }

  public handleSubmit = async data => {
    const {
      dasherId,
      onActivate,
      activateRedCard,
      getRedCard,
      clearErrors,
      trackRedCardActivateSubmit,
      trackRedCardActivateError,
    } = this.props
    try {
      clearErrors()
      await activateRedCard(dasherId, data.last4, data.delightNumber)
      await trackRedCardActivateSubmit()
      await getRedCard(dasherId)
      onActivate()
    } catch (error) {
      trackRedCardActivateError(error)
    }
  }

  public handleFocus = () => {
    window.scrollTo(0, 160)
    document.body.scrollTop = 160
  }

  public handleBack = () => {
    const { returnLink, trackRedCardActivateGoBack, history } = this.props
    trackRedCardActivateGoBack()
    if (returnLink) {
      if (typeof returnLink === 'string') {
        history.push(returnLink)
      } else {
        returnLink()
      }
    }
  }

  public handleLearnMoreClicked = e => {
    e.stopPropagation()

    const isShowingModal = !this.state.isShowingModal
    this.setState({
      isShowingModal,
    })

    const { trackRedCardActivateLearnMore } = this.props
    trackRedCardActivateLearnMore(`${isShowingModal ? 'opened' : 'closed'}`)
  }

  public render() {
    const { redCardIsSubmitting, error, experiments, returnLink } = this.props
    return (
      <React.Fragment>
        {experiments.instant_dashing_v2 && (
          <NavBar
            navigationType={NavBar.NavigationTypes.Back}
            isSeparatorVisible={false}
            onNavigationClick={this.handleBack}
          />
        )}
        <Wrapper
          hasError={error}
          instant_dashing_v2={experiments.instant_dashing_v2}
        >
          <StackChildren>
            {experiments.instant_dashing_v2 ? (
              <React.Fragment>
                <Text
                  styles={Text.Styles.Bold24}
                  lineHeight={Text.LineHeights.S24}
                >
                  {t.idv2Title()}
                </Text>
                <Text
                  styles={Text.Styles.ContentAction}
                  size={Text.Sizes.S14}
                  weight={Text.Weights.Bold}
                  tag="a"
                  href="#"
                  onClick={e => this.handleLearnMoreClicked(e)}
                >
                  {t.idv2LearnMore()}
                </Text>
              </React.Fragment>
            ) : (
              <React.Fragment>
                <Text styles={Text.Styles.Bold32}>{t.title()}</Text>
                <Text styles={Text.Styles.SectionSubtext}>
                  {t.description()}
                </Text>
                <Text
                  styles={Text.Styles.ContentAction}
                  size={Text.Sizes.S14}
                  tag="a"
                  href="#"
                  onClick={e => this.handleLearnMoreClicked(e)}
                >
                  {t.learnMore()}
                </Text>
              </React.Fragment>
            )}
            {this.state.isShowingModal && (
              <LearnMoreModal
                onClose={this.handleLearnMoreClicked.bind(this)}
              />
            )}
            <InlineChildren
              justifyContent={InlineChildren.Justification.Center}
            >
              <RedCard
                withText
                lastfour={t.cardLastFour()}
                delightnumber={t.cardDelightNumber()}
              />
            </InlineChildren>
            <Stack size={Stack.Sizes.Large} />
            <Form
              onSubmit={this.handleSubmit}
              validators={RedCardActivate.validators}
              fields={RedCardActivate.fields}
            >
              {(fields, handleChange, handleSubmit, { isLoading }) => (
                <form onSubmit={handleSubmit}>
                  <StackChildren>
                    <NumberField
                      type="number"
                      label={t.labelLastFour()}
                      id="last4"
                      max={9999}
                      placeholder="2345"
                      onChange={handleChange.bind(this, 'last4')}
                      onBlur={this.handleFocus}
                      autoComplete="off"
                      {...fields.last4}
                    />
                    <NumberField
                      type="number"
                      label={t.labelDelightNumber()}
                      id="delightNumber"
                      max={9999999999}
                      placeholder="000000"
                      onChange={handleChange.bind(this, 'delightNumber')}
                      onBlur={this.handleFocus}
                      autoComplete="off"
                      {...fields.delightNumber}
                    />
                    {this.state.showSubmitButton && (
                      <Bottom>
                        <Button
                          state={
                            isLoading || redCardIsSubmitting
                              ? Button.States.Loading
                              : Button.States.Default
                          }
                          onClick={async () => {
                            try {
                              await handleSubmit()
                            } catch (e) {
                              console.warn(e)
                            }
                          }}
                          size={Button.Sizes.Large}
                          type={
                            experiments.instant_dashing_v2
                              ? Button.Types.PrimaryPill
                              : Button.Types.Primary
                          }
                        >
                          {t.activateButton()}
                        </Button>
                        {!experiments.instant_dashing_v2 && returnLink && (
                          <Button
                            type={Button.Types.FlatPrimary}
                            onClick={this.handleBack}
                          >
                            {t.goBack()}
                          </Button>
                        )}
                      </Bottom>
                    )}
                  </StackChildren>
                </form>
              )}
            </Form>
          </StackChildren>
        </Wrapper>
      </React.Fragment>
    )
  }
}

const mapStateToProps = state => ({
  experiments: state.experiments,
  redCardIsSubmitting: state.redCardActivation.isSubmitting,
  error: state.errorMessage,
})

const mapDispatchToProps = {
  activateRedCard: actions.activateRedCard,
  getRedCard: actions.getRedCard,
  clearErrors: actions.clearErrors,
  trackRedCardActivateRendered: events.trackRedCardActivateRendered,
  trackRedCardActivateSubmit: events.trackRedCardActivateSubmit,
  trackRedCardActivateError: events.trackRedCardActivateError,
  trackRedCardActivateGoBack: events.trackRedCardActivateGoBack,
  trackRedCardActivateLearnMore: events.trackRedCardActivateLearnMore,
}

export default withRouter<RedCardActivateType & RouteComponentProps<any>>(
  connect(mapStateToProps, mapDispatchToProps)(RedCardActivate)
)
